import { useFormContext, Controller, type FieldValues, type Path } from 'react-hook-form';
import { DatePicker } from 'pink-lava-ui';
import dayjs, { Dayjs } from 'dayjs';
import type { roundedType } from 'pink-lava-ui/types';

export const DateRangeInput = <T extends FieldValues = any>({
  name,
  label,
  disabled,
  required,
  showErrorMessage,
  dateFormat = {
    format: 'DD MMM YYYY',
    type: 'mask',
  },
  onChange,
  onChangeCallback,
  info,
  variant = 'outlined',
  startPlaceholder = 'Start date',
  endPlaceholder = 'End date',
  rounded,
  className = '',

  size: _size = 'middle',
  minDate,
  maxDate,
}: {
  name: string | Path<T>;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  showErrorMessage?: boolean;
  dateFormat?: {
    format: string;
    type?: 'mask';
  };
  onChange?: (
    _dates: [Dayjs | null, Dayjs | null] | null,
    _fieldOnChange: (_dates: [Dayjs | null, Dayjs | null] | null) => void
  ) => void;
  onChangeCallback?: (
    _dates: [Dayjs | null, Dayjs | null] | null,
    _setValue: (_name: keyof T & string, _value: any) => void
  ) => void;
  info?: string;
  variant?: 'filled' | 'borderless' | 'underlined' | 'outlined';
  startPlaceholder?: string;
  endPlaceholder?: string;
  rounded?: roundedType;
  className?: string;
  size?: 'small' | 'middle' | 'large';
  minDate?: Dayjs | string | null;
  maxDate?: Dayjs | string | null;
}) => {
  const { control, setValue } = useFormContext();

  return (
    <>
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field, fieldState: { error } }) => {
          const paramsValue = field?.value
            ? Array.isArray(field.value)
              ? field.value.length === 2
                ? (field.value.map((val: any) => (val ? (typeof val === 'string' ? dayjs(val) : val) : null)) as [
                    Dayjs | null,
                    Dayjs | null,
                  ])
                : ([null, null] as [Dayjs | null, Dayjs | null])
              : ([null, null] as [Dayjs | null, Dayjs | null])
            : ([null, null] as [Dayjs | null, Dayjs | null]);

          const handleDateChange = (
            date: Dayjs | null,
            position: 'start' | 'end',
            currentValues: [Dayjs | null, Dayjs | null]
          ) => {
            const newValues: [Dayjs | null, Dayjs | null] = [...currentValues];
            if (position === 'start') {
              newValues[0] = date;
            } else {
              newValues[1] = date;
            }

            // If both are null, we might want to set null or maintain [null, null]
            // The original expected [Dayjs | null, Dayjs | null] | null
            // We will stick to [Dayjs | null, Dayjs | null] to keep it consistent with the array structure

            const finalValue = newValues; // as [Dayjs | null, Dayjs | null]

            console.log(`DatePicker (${position}) onChange called with:`, date);

            if (onChange) {
              onChange(finalValue, field.onChange);
            } else {
              field.onChange(finalValue);
              onChangeCallback?.(finalValue, setValue);
            }
          };

          const commonProps = {
            variant,
            format: dateFormat,
            disabled,
            allowClear: true,
            classNames: { root: 'w-full!' },
            className: `${rounded === 'large' ? '!rounded-full' : rounded ? '!rounded-lg' : ''} ${className} ${showErrorMessage && error?.message ? 'border-red-500!' : ''}`,
          };

          return (
            <div className="gap-2 w-full">
              {label && (
                <label className="block text-sm text-gray-700 mb-1">
                  {label}
                  {required && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}
              <div className="flex gap-2 w-full">
                <div className="flex-1">
                  <DatePicker
                    {...commonProps}
                    size={'small'}
                    placeholder={startPlaceholder}
                    rounded={rounded}
                    value={paramsValue[0]}
                    minDate={minDate ? (typeof minDate === 'string' ? dayjs(minDate) : minDate) : undefined}
                    maxDate={
                      paramsValue[1]
                        ? paramsValue[1]
                        : maxDate
                          ? typeof maxDate === 'string'
                            ? dayjs(maxDate)
                            : maxDate
                          : undefined
                    }
                    onChange={(date: Dayjs | null) => handleDateChange(date, 'start', paramsValue)}
                    onBlur={field.onBlur}
                    name={`${field.name}.start`}
                  />
                </div>
                <div className="flex-1">
                  <DatePicker
                    {...commonProps}
                    size={'small'}
                    placeholder={endPlaceholder}
                    rounded={rounded}
                    value={paramsValue[1]}
                    minDate={
                      paramsValue[0]
                        ? paramsValue[0]
                        : minDate
                          ? typeof minDate === 'string'
                            ? dayjs(minDate)
                            : minDate
                          : undefined
                    }
                    maxDate={maxDate ? (typeof maxDate === 'string' ? dayjs(maxDate) : maxDate) : undefined}
                    onChange={(date: Dayjs | null) => handleDateChange(date, 'end', paramsValue)}
                    onBlur={field.onBlur}
                    name={`${field.name}.end`}
                  />
                </div>
              </div>

              {info && <p className="text-gray-500 text-xs mt-1">{info}</p>}
              {showErrorMessage && error?.message && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
            </div>
          );
        }}
      />
    </>
  );
};
